import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stepper, Step, StepLabel, Box, CircularProgress, Alert
} from '@mui/material';
import PassportUploadStep from './steps/PassportUploadStep';
import ConfirmExtractedStep from './steps/ConfirmExtractedStep';
import HealthFormStep from './steps/HealthFormStep';
import PassDisplayStep from './steps/PassDisplayStep';
import { travelerScreeningAPI } from '../../services/api';

const STEPS = ['Upload Passport', 'Confirm Fields', 'Health Screening', 'Pass'];
const HEALTH_SUB_TABS = ['Entry Info', 'Identity', 'Health', 'Vaccination'];

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const calcAge = (dob: string): number | null => {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age > 0 ? age : null;
};

const AddPassengerDialog: React.FC<Props> = ({ open, onClose, onSaved }) => {
  const [activeStep,    setActiveStep]    = useState(0);
  const [healthSubTab,  setHealthSubTab]  = useState(0);
  const [extracted,     setExtracted]     = useState<Record<string, unknown>>({});
  const [confirmed,     setConfirmed]     = useState<Record<string, string>>({});
  const [form,          setForm]          = useState<Record<string, unknown>>({});
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState<string | null>(null);
  const [savedScreening, setSavedScreening] = useState<Record<string, unknown> | null>(null);

  const handleExtracted = (data: Record<string, unknown>) => {
    setExtracted(data);
    const dob = (data.date_of_birth as string) || '';
    const age = calcAge(dob);
    setForm(prev => ({
      ...prev,
      full_name:      data.full_name      || '',
      id_number:      data.doc_number     || '',
      nationality:    data.nationality    || '',
      date_of_birth:  dob,
      age:            age ?? '',
      sex:            data.sex            || '',
      id_type:        'passport',
      doc_file_url:   data.doc_file_url   || '',
      mrz_line1:      data.mrz_line1      || '',
      mrz_line2:      data.mrz_line2      || '',
      ocr_confidence: data.ocr_confidence || 0,
      ocr_extracted: {
        raw_text:        data.raw_text,
        issuing_country: data.issuing_country,
        expiry_date:     data.expiry_date,
      },
    }));
    setActiveStep(1);
  };

  const handleConfirmedChange = (field: string, value: string) => {
    setConfirmed(prev => ({ ...prev, [field]: value }));
    const directMap: Record<string, string> = {
      full_name:     'full_name',
      doc_number:    'id_number',
      nationality:   'nationality',
      sex:           'sex',
    };
    if (field === 'date_of_birth') {
      const age = calcAge(value);
      setForm(prev => ({ ...prev, date_of_birth: value, age: age ?? '' }));
    } else if (directMap[field]) {
      setForm(prev => ({ ...prev, [directMap[field]]: value }));
    } else {
      setForm(prev => ({
        ...prev,
        ocr_extracted: { ...(prev.ocr_extracted as Record<string, unknown>), [field]: value },
      }));
    }
  };

  const handleFormChange = (field: string, value: unknown) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        full_name:     confirmed.full_name     || form.full_name,
        id_number:     confirmed.doc_number    || form.id_number,
        nationality:   confirmed.nationality   || form.nationality,
        date_of_birth: confirmed.date_of_birth || form.date_of_birth,
        sex:           confirmed.sex           || form.sex,
      };
      const cleaned = Object.fromEntries(
        Object.entries(payload).map(([k, v]) => [k, v === '' ? null : v])
      );
      const result = await travelerScreeningAPI.createScreening(cleaned);
      setSavedScreening(result);
      setActiveStep(3);
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(msg || 'Failed to save screening. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setHealthSubTab(0);
    setExtracted({});
    setConfirmed({});
    setForm({});
    setSavedScreening(null);
    setError(null);
    onClose();
  };

  const isLastHealthTab = healthSubTab === HEALTH_SUB_TABS.length - 1;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Passenger</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map(label => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ minHeight: 320 }}>
          {activeStep === 0 && <PassportUploadStep onExtracted={handleExtracted} />}
          {activeStep === 1 && (
            <ConfirmExtractedStep
              extracted={extracted}
              confirmed={confirmed}
              onChange={handleConfirmedChange}
            />
          )}
          {activeStep === 2 && (
            <HealthFormStep
              form={form}
              onChange={handleFormChange}
              subTab={healthSubTab}
              subTabLabels={HEALTH_SUB_TABS}
            />
          )}
          {activeStep === 3 && savedScreening && (
            <PassDisplayStep screening={savedScreening} />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {/* Cancel / Save */}
        {activeStep < 3 && (
          <Button onClick={handleClose} color="inherit">Cancel</Button>
        )}

        {/* Step 1 — Confirm fields */}
        {activeStep === 1 && (
          <Button variant="contained" onClick={() => { setHealthSubTab(0); setActiveStep(2); }}>
            Next →
          </Button>
        )}

        {/* Step 2 — Health form sub-tab navigation */}
        {activeStep === 2 && healthSubTab > 0 && (
          <Button onClick={() => setHealthSubTab(t => t - 1)}>← Back</Button>
        )}
        {activeStep === 2 && !isLastHealthTab && (
          <Button variant="contained" onClick={() => setHealthSubTab(t => t + 1)}>
            Next →
          </Button>
        )}
        {activeStep === 2 && isLastHealthTab && (
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Generate Pass'}
          </Button>
        )}

        {/* Step 3 — pass is already saved; close dialog */}
        {activeStep === 3 && (
          <Button variant="contained" color="success" onClick={handleClose}>
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddPassengerDialog;
